import React, { memo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import content from "../data/content.json";
import { resolveAssetPath, sortByOrderThenId } from "../utils/content";
import "./Projects.css";

const projectsData = [...(content.projects ?? [])].sort(sortByOrderThenId);

const ProjectCard = memo(({ project, index, progress, targetScale, total }) => {
  const range = [index / total, 1];
  const scale = useTransform(progress, range, [1, targetScale]);
  const projectImage = resolveAssetPath(project.image);
  const showLiveButton = Boolean(project.link) && project.show_live_button !== false;

  return (
    <div className="proj-card-slot">
      <motion.div
        className="proj-card glassmorphism"
        style={{
          scale,
          "--proj-card-top": `calc(15vh + ${index * 30}px)`
        }}
      >
        <div className="proj-card-header">
          <span className="proj-badge" style={{ color: project.color, borderColor: project.color }}>
            {project.category} &bull; {project.year}
          </span>
        </div>

        {projectImage ? (
          <div className="proj-cover">
            <img src={projectImage} alt={project.title} loading="lazy" decoding="async" />
          </div>
        ) : null}

        <div className="proj-card-content">
          <h3 className="proj-title">{project.title}</h3>

          <div className="proj-details">
            <p className="proj-desc">{project.description}</p>

            <ul className="proj-features">
              {(project.features || []).map((feature, featureIndex) => (
                <li key={`${project.id || project.title}-${featureIndex}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke={project.color} strokeWidth="2" />
                    <path d="M8 12L11 15L16 9" stroke={project.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="proj-card-footer">
          {showLiveButton ? (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="proj-cta-button"
              style={{ backgroundColor: project.color }}
            >
              View Live Site
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          ) : null}
        </div>

        <div className="proj-card-glow" style={{ background: `radial-gradient(circle at 120% 2%, ${project.color}33 0%, transparent 70%)` }} />
      </motion.div>
    </div>
  );
});

const Projects = () => {
  const containerRef = useRef(null);
  const totalProjects = projectsData.length || 1;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  return (
    <section className="proj-section" id="projects" ref={containerRef}>
      <div className="proj-ambient-glow" />

      <div className="proj-header-sticky">
        <div className="proj-header-inner">
          <h2 className="proj-heading">PROJECTS</h2>
        </div>
      </div>

      <div className="proj-cards-container">
        {projectsData.map((project, index) => {
          const targetScale = 1 - (totalProjects - index) * 0.05;

          return (
            <ProjectCard
              key={project.id ?? `${project.title}-${index}`}
              project={project}
              index={index}
              progress={scrollYProgress}
              targetScale={targetScale}
              total={totalProjects}
            />
          );
        })}
      </div>
    </section>
  );
};

export default Projects;
